import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../AuthContext';
import { getSubscriptionPlans, selectSubscriptionPlan, createSubscriptionOrder, verifySubscriptionPayment } from '../../../api/SubscriptionAPI';
import './SelectPlanPage.css';

const SelectPlanPage = () => {
    const { user, isLoading: isAuthLoading } = useContext(AuthContext);
    const [plans, setPlans] = useState([]);
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        if (!isAuthLoading) {
            getSubscriptionPlans((response) => {
                setIsLoading(false);
                if (response.plans) {
                    setPlans(response.plans);
                } else {
                    setError('Could not fetch subscription plans.');
                }
            });
        }
    }, [isAuthLoading]);

    const handleSelectPlan = (planId) => {
        setSelectedPlanId(planId);
    };

    const handleSubmit = () => {
        if (!selectedPlanId) {
            setError('Please select a plan to continue.');
            return;
        }
        
        const selectedPlan = plans.find(p => p.id === selectedPlanId);
        if (!selectedPlan) return;

        setIsLoading(true);
        setError('');

        // If the plan is free (like the trial), use the original logic.
        if (selectedPlan.price <= 0) {
            selectSubscriptionPlan(selectedPlanId, (response) => {
                setIsLoading(false);
                if (response.subscription) {
                    // Use window.location.href to force a full page reload and update user context
                    window.location.href = '/club_portal/dashboard';
                } else {
                    setError(response?.message || 'An error occurred while selecting the plan.');
                }
            });
            return;
        }

        // --- START RAZORPAY LOGIC FOR PAID PLANS ---
        createSubscriptionOrder(selectedPlanId, (orderResponse) => {
            if (!orderResponse.order_id) {
                setIsLoading(false);
                setError(orderResponse.message || 'Could not initiate payment.');
                return;
            }

            const options = {
                key: 'rzp_test_RT3XdrpzZf4lfp', // Your Razorpay Test Key ID
                amount: orderResponse.amount,
                currency: "INR",
                name: "Cricket Manager Subscription",
                description: `Payment for ${orderResponse.plan.name}`,
                order_id: orderResponse.order_id,
                handler: function (response) {
                    // This function is called on successful payment
                    setIsLoading(true);
                    verifySubscriptionPayment({
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_signature: response.razorpay_signature
                    }, (verifyResponse) => {
                        if (verifyResponse.subscription) {
                            // Use window.location.href for a full reload to update user context
                            window.location.href = '/club_portal/dashboard';
                        } else {
                            setIsLoading(false);
                            setError('Payment verification failed. Please contact support.');
                        }
                    });
                },
                prefill: {
                    name: user.name,
                    contact: user.phone_number
                },
                notes: {
                    address: "Cricket Manager Corp."
                },
                theme: {
                    color: "#28a745"
                },
                modal: {
                    ondismiss: function() {
                        // Re-enable the button if user closes the modal without paying
                        setIsLoading(false);
                    }
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
        });
        // --- END RAZORPAY LOGIC ---
    };

    const getIntervalText = (days) => {
        if (days <= 14) return '';
        if (days <= 31) return '/ mo';
        if (days <= 92) return '/ qtr';
        if (days >= 365) return '/ yr';
        return '';
    };
    
    if (isAuthLoading || isLoading) {
        return (
            <div className='plan-page-bg d-flex justify-content-center align-items-center'>
                <div className="spinner-border text-success" style={{ width: '3rem', height: '3rem' }} role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="plan-page-bg">
            <div className="container py-5">
                <div className="text-center plan-page-header mb-5">
                    <h1>Choose Your Plan</h1>
                    <p className="lead">
                        Welcome, {user?.name}! Select a plan for <strong>{user?.club?.name}</strong> to get started.
                    </p>
                </div>

                {error && <div className="alert alert-danger mx-auto" style={{maxWidth: '800px'}}>{error}</div>}

                <div className="row justify-content-center">
                    {plans.map((plan) => {
                        const isRecommended = plan.duration_in_days === 365;
                        return (
                            <div key={plan.id} className="col-lg-3 col-md-6 mb-4">
                                <div
                                    className={`plan-card text-center ${selectedPlanId === plan.id ? 'selected' : ''} ${isRecommended ? 'recommended' : ''}`}
                                    onClick={() => handleSelectPlan(plan.id)}
                                >
                                    {isRecommended && <div className="recommended-badge">Best Value</div>}
                                    
                                    <div className="plan-header">
                                        <h4 className="plan-title">{plan.name}</h4>
                                        <h2 className="plan-price">
                                            {plan.price > 0 ? `₹${plan.price}` : 'Free'}
                                            <span className="interval">{getIntervalText(plan.duration_in_days)}</span>
                                        </h2>
                                    </div>
                                    <div className="plan-body">
                                        <p className="plan-duration">{plan.duration_in_days} Days Access</p>
                                        <p className="plan-description">{plan.description}</p>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>

                <div className="text-center mt-4">
                    <button
                        className="btn btn-success btn-lg"
                        onClick={handleSubmit}
                        disabled={!selectedPlanId || isLoading}
                    >
                        {isLoading ? 'Processing...' : 'Activate Plan'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SelectPlanPage;